import { useMemo } from 'react';
import { getPropertyValue, AVAILABLE_PROPERTIES } from './matrixClassification';

/**
 * Display order for the Low/Medium/High level suffix found in per-class
 * stem property keys (e.g. 'stems_low:coniferous'). Properties without a
 * level (e.g. 'hectare_equivalent:coniferous') sort ahead of leveled ones.
 */
const LEVEL_ORDER = { low: 0, medium: 1, high: 2 };

/**
 * Dynamic property key patterns for per-class enriched metadata.
 * Each entry defines how to detect and label a key pattern.
 *
 * IMPORTANT: Order matters. More specific prefixes (e.g. 'stems_per_ha_')
 * must appear before less specific ones (e.g. 'stems_') because matching
 * stops at the first hit. Reordering may cause keys to be misclassified.
 */
const DYNAMIC_KEY_PATTERNS = [
	{
		prefix: 'hectare_equivalent:',
		getLabel: className => `Ha/eq (${className})`,
		unit: '/ha',
	},
	{
		prefix: 'stems_per_ha_',
		requiresColon: true,
		getLabel: (className, level) => `Stems/ha ${level} (${className})`,
		unit: '/ha',
	},
	{
		prefix: 'stems_',
		requiresColon: true,
		// Must come after 'stems_per_ha_' to avoid matching those keys
		excludePrefix: 'stems_per_ha_',
		getLabel: (className, level) => `Stems ${level} (${className})`,
		unit: 'pcs',
	},
];

/**
 * Attempts to parse a metadata key as a dynamic per-class property.
 * @returns {{ id, label, unit, patternIndex, level, className }} or null if
 * the key doesn't match any pattern. `patternIndex`, `level` and
 * `className` are used to sort results in a stable, human-friendly order
 * (grouped by property type, then Low → Medium → High, then class name)
 * instead of a plain alphabetical label sort.
 */
function parseDynamicKey(key) {
	for (let patternIndex = 0; patternIndex < DYNAMIC_KEY_PATTERNS.length; patternIndex++) {
		const pattern = DYNAMIC_KEY_PATTERNS[patternIndex];
		if (!key.startsWith(pattern.prefix)) continue;
		if (pattern.excludePrefix && key.startsWith(pattern.excludePrefix)) continue;
		if (pattern.requiresColon && !key.includes(':')) continue;

		const rest = key.slice(pattern.prefix.length);

		if (pattern.requiresColon) {
			const colonIdx = rest.indexOf(':');
			const level = rest.slice(0, colonIdx);
			const className = rest.slice(colonIdx + 1);
			const levelLabel = level.charAt(0).toUpperCase() + level.slice(1);
			return {
				id: key,
				label: pattern.getLabel(className, levelLabel),
				unit: pattern.unit,
				patternIndex,
				level: level.toLowerCase(),
				className,
			};
		}

		// Simple prefix:className pattern (e.g. hectare_equivalent:coniferous)
		return {
			id: key,
			label: pattern.getLabel(rest),
			unit: pattern.unit,
			patternIndex,
			level: null,
			className: rest,
		};
	}
	return null;
}

/**
 * Comparator for dynamic per-class properties. Groups by property type
 * (patternIndex), then orders Low → Medium → High within each type, then
 * falls back to class name for ties (e.g. coniferous vs leaf).
 */
function compareDynamicProperties(a, b) {
	if (a.patternIndex !== b.patternIndex) return a.patternIndex - b.patternIndex;
	const levelRankA = a.level in LEVEL_ORDER ? LEVEL_ORDER[a.level] : -1;
	const levelRankB = b.level in LEVEL_ORDER ? LEVEL_ORDER[b.level] : -1;
	if (levelRankA !== levelRankB) return levelRankA - levelRankB;
	return a.className.localeCompare(b.className);
}

/** A metadata value counts as data only when it is a usable number. */
const hasValue = val => val != null && !isNaN(val);

/**
 * Derive available properties from actual feature data.
 *
 * Currently, scans feature metadata client-side to determine which
 * properties have data, then returns only the matching entries from
 * AVAILABLE_PROPERTIES.
 *
 * For DSP features with multi-class detection models, this hook also
 * discovers dynamic per-class hectare equivalent properties (keys matching
 * 'hectare_equivalent:<className>') and appends them to the result.
 * When per-class options exist, the total is relabeled "Ha/eq (Total)"
 * to distinguish it from per-class entries like "Ha/eq (Tall)".
 *
 * ## API Integration (Requirement 6)
 *
 * When the backend implements Requirement 6, the hexagon features
 * response (POST /projects/{projectId}/hexagons/{taskId}/{resolution})
 * will include an `available_properties` array:
 *
 * ```json
 * {
 *   "hexagon_data": { ... },
 *   "available_properties": [
 *     { "id": "mean_height", "label": "Mean Height", "unit": "m" },
 *     { "id": "count", "label": "Count", "unit": "pcs" }
 *   ]
 * }
 * ```
 *
 * To integrate:
 * 1. Add `hexagonInfo` as a parameter to this hook
 * 2. If `hexagonInfo.available_properties` exists, return it directly
 * 3. Fall back to the current client-side scanning when the field is absent
 * 4. Remove the client-side scanning once all environments return the field
 *
 * ## Readiness
 *
 * Until real metadata has been scanned the hook returns the full static list as
 * a placeholder, which omits every dynamic per-class key, so callers that write
 * state from the list (e.g. reconciling a selected property) must gate on
 * `isReady`.
 *
 * @param {Array} features - Array of OpenLayers features (hexagons or DSP plots)
 * @returns {{ properties: Array<{id: string, label: string, unit: string}>, isReady: boolean }}
 */
export default function useAvailableProperties(features) {
	// TODO: When backend ships Requirement 6, accept hexagonInfo param and
	// return hexagonInfo.available_properties when present, e.g.:
	//
	// export default function useAvailableProperties(features, hexagonInfo) {
	//   if (hexagonInfo?.available_properties?.length) {
	//     return { properties: hexagonInfo.available_properties, isReady: true };
	//   }
	//   // ...fall back to client-side scanning below
	// }

	return useMemo(() => {
		// Scan every feature, not a sample: a list reported as ready must be
		// complete or callers would clear valid selections.
		const foundIds = new Set();
		const dynamicByKey = new Map();

		for (const feature of features ?? []) {
			const meta = feature.get('data')?.metadata;
			if (!meta) continue;

			// Check static properties
			for (const prop of AVAILABLE_PROPERTIES) {
				if (foundIds.has(prop.id)) continue;
				if (hasValue(getPropertyValue(meta, prop.id))) foundIds.add(prop.id);
			}

			// Discover dynamic per-class properties, flattened into metadata during
			// enrichment as 'hectare_equivalent:<className>' and 'stems_*:<className>'.
			for (const key of Object.keys(meta)) {
				if (dynamicByKey.has(key) || !hasValue(meta[key])) continue;
				const parsed = parseDynamicKey(key);
				if (parsed) dynamicByKey.set(key, parsed);
			}
		}

		// No usable values yet: the static list stands in as a placeholder.
		if (foundIds.size === 0 && dynamicByKey.size === 0) {
			return { properties: AVAILABLE_PROPERTIES, isReady: false };
		}

		// With per-class options present, the static total is relabeled to
		// tell it apart from entries like "Ha/eq (Tall)".
		const hasPerClass = dynamicByKey.size > 0;
		const statics = AVAILABLE_PROPERTIES.filter(p => foundIds.has(p.id)).map(p =>
			hasPerClass && p.id === 'hectare_equivalent' ? { ...p, label: 'Ha/eq (Total)' } : p
		);
		// Sorting needs patternIndex/level/className; strip them afterwards.
		const dynamic = [...dynamicByKey.values()]
			.sort(compareDynamicProperties)
			.map(({ id, label, unit }) => ({ id, label, unit }));

		return { properties: [...statics, ...dynamic], isReady: true };
	}, [features]);
}
